import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import AddressSearchComponent from './AddressSearchComponent';
import GeolocationComponent from './GeolocationComponent';
import DesktopNavComponent from './DesktopNavComponent';
import MobileNavComponent from './MobileNavComponent';
import NavItemsComponent from './NavItemsComponent';
import { MegaMenuManager } from '../mega-menus';
import { searchAddress, getPlaceDetails, checkCoverage } from '../api';

/**
 * NavigationBar - Main navigation component
 * 
 * This component has been refactored to break down the original large component
 * into smaller, more manageable components organized by functionality.
 */
const NavigationBar = ({
  openCoverageSidebar,
  isUsingGeolocation = false,
  setIsUsingGeolocation = () => {},
  mobileMenuAction,
  setMobileMenuAction,
  isHidden = false,
  openComparisonModal = null,
  openRecommendationModal = null,
  openWirelessAdvisorModal = null,
  openRouterFeaturesModal = null,
  openISPFeaturesModal = null
}) => {
  // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [geolocationRequested, setGeolocationRequested] = useState(false);
  const [isCheckingCoverage, setIsCheckingCoverage] = useState(false);
  const [isLoadingGeolocation, setIsLoadingGeolocation] = useState(false);
  
  // Refs
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const location = useLocation();
  
  // Close menus when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setActiveMenu(null);
    setSearchResults([]);
    setSearchQuery('');
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  
  // Handle click outside search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchResults([]);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle address search
  useEffect(() => {
    // Skip search if an address was just selected
    if (isAddressSelected) {
      setIsAddressSelected(false);
      return;
    }
    
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.length >= 4 && !isUsingGeolocation) { // Minimum 4 characters as per API requirement
        setIsSearching(true);
        try {
          const response = await searchAddress(searchQuery);
          setSearchResults(response.results);
        } catch (error) {
          console.error('Error searching for address:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, isAddressSelected, isUsingGeolocation]);
  
  // Handler functions
  const handleAddressSelect = async (address) => {
    setSelectedAddress(address);
    setIsAddressSelected(true); // Set flag to prevent search from triggering
    setSearchQuery(address.description || address.mainText);
    setSearchResults([]); // Clear search results immediately
    setIsUsingGeolocation(false);
    
    if (searchInputRef.current) {
      searchInputRef.current.disabled = false;
    }
    
    try {
      setIsCheckingCoverage(true);
      const placeDetails = await getPlaceDetails(address.placeId);
      
      if (placeDetails && placeDetails.geometry && placeDetails.geometry.location) {
        // Attach the placeDetails to the address object
        const enrichedAddress = {
          ...address,
          placeDetails: placeDetails
        };
        
        // Open coverage sidebar with the selected address
        openCoverageSidebar(placeDetails.geometry.location, enrichedAddress);
      }
    } catch (error) {
      console.error('Error getting place details:', error);
    } finally {
      setIsCheckingCoverage(false);
    }
  };
  
  const handleGeolocationCoverageCheck = async (coordinates) => {
    if (!coordinates) return;
    
    setIsCheckingCoverage(true);
    
    try {
      const result = await checkCoverage(coordinates);
      
      // Create a placeholder address object for the geolocation
      const geoAddress = {
        description: "Your current location (approximate)",
        mainText: "Current Location",
        secondaryText: "Using GPS coordinates (approximate)",
        // Include placeDetails with geometry and location
        placeDetails: {
          geometry: {
            location: coordinates
          },
          // Add a default province name based on the coverage result
          address_components: [
            {
              types: ["administrative_area_level_1"],
              long_name: result?.province || "Gauteng" // Default to Gauteng if province not available
            }
          ]
        }
      };
      
      // Open coverage sidebar with the geolocation result
      openCoverageSidebar(coordinates, geoAddress);
    } catch (error) {
      console.error('Error checking coverage with geolocation:', error);
    } finally {
      setIsCheckingCoverage(false);
    }
  };
  
  const handleClearSearch = () => {
    setSearchQuery('');
    setSelectedAddress(null);
    setIsAddressSelected(false);
    setIsUsingGeolocation(false);
    setSearchResults([]);
    if (searchInputRef.current) {
      searchInputRef.current.disabled = false;
      searchInputRef.current.focus();
    }
  };
  
  const toggleMenu = (menuName) => {
    if (activeMenu === menuName) {
      // If clicking the same menu item, close it
      setActiveMenu(null);
      setIsMenuOpen(false);
    } else {
      // If clicking a different menu item, open it
      setActiveMenu(menuName);
      setIsMenuOpen(true);
    }
  };
  
  const handleMobileMenuAction = (action) => {
    if (action === 'menu') {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    } else if (action === 'coverage') {
      openCoverageSidebar();
    }
  };

  // Main navigation items
  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Packages', menuId: 'packages' },
    { name: 'About Us', menuId: 'about' },
  ];
  
  return (
    <>
      {/* Desktop Navigation */}
      <DesktopNavComponent
        navItems={navItems}
        activeMenu={activeMenu}
        toggleMenu={toggleMenu}
        searchContainerRef={searchContainerRef}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setIsAddressSelected={setIsAddressSelected}
        isLoadingGeolocation={isLoadingGeolocation}
        handleClearSearch={handleClearSearch}
        searchResults={searchResults}
        isSearching={isSearching}
        handleAddressSelect={handleAddressSelect}
        isCheckingCoverage={isCheckingCoverage}
        openCoverageSidebar={openCoverageSidebar}
        isHidden={isHidden}
      />
      
      {/* Mobile Navigation */}
      <MobileNavComponent
        navItems={navItems}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isCheckingCoverage={isCheckingCoverage}
        openCoverageSidebar={openCoverageSidebar}
        handleMobileMenuAction={handleMobileMenuAction}
        isHidden={isHidden}
      />
      
      {/* Geolocation Component */}
      <GeolocationComponent
        geolocationRequested={geolocationRequested}
        setGeolocationRequested={setGeolocationRequested}
        setIsUsingGeolocation={setIsUsingGeolocation}
        setIsLoadingGeolocation={setIsLoadingGeolocation}
        handleGeolocationCoverageCheck={handleGeolocationCoverageCheck}
        setSearchQuery={setSearchQuery}
        searchInputRef={searchInputRef}
      />
      
      {/* Mega Menu Manager */}
      <MegaMenuManager
        activeMenu={activeMenu}
        onClose={() => toggleMenu(activeMenu)}
        openComparisonModal={openComparisonModal}
        openRecommendationModal={openRecommendationModal}
        openWirelessAdvisorModal={openWirelessAdvisorModal}
        openRouterFeaturesModal={openRouterFeaturesModal}
        openISPFeaturesModal={openISPFeaturesModal}
        openCoverageSidebar={openCoverageSidebar}
      />
    </>
  );
};

export default NavigationBar;

// Export all components for direct imports
export { default as AddressSearchComponent } from './AddressSearchComponent';
export { default as GeolocationComponent } from './GeolocationComponent';
export { default as DesktopNavComponent } from './DesktopNavComponent';
export { default as MobileNavComponent } from './MobileNavComponent';
export { default as NavItemsComponent } from './NavItemsComponent';